import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useMemo, memo } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import useProductAvailableSizes from 'data-hooks/useProductAvailableSizes';
import useAvailableSizesProductsDetails from 'data-hooks/useAvailableSizesProductsDetails';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import useModularPDPAvailableSizes from 'data-hooks/useModularPDPAvailableSizes';
import useModularDetails from 'data-hooks/useModularDetails';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import PDPAvailableInSizesSkeleton from './PDPAvailableInSizesSkeleton';
import PDPAvailableInSizesUI from './PDPAvailableInSizesUI';
import usePdpIsModularBYOProduct from '../state/context/usePdpIsModularBYOProduct';
const isAddOnProduct = lowercaseName => lowercaseName.includes('add-on') || lowercaseName.includes('add on') || lowercaseName.includes('addon');
const PDPAvailableInSizesInner = ({
  slug,
  isExpanded,
  onToggleExpanded
}) => {
  const {
    state: routerState
  } = useLocation();
  const routerHasProductData = routerState?.productsData?.length;
  const routerInitialIsExpanded = !!routerState?.initialIsExpanded;
  useEffect(() => {
    if (routerInitialIsExpanded) {
      onToggleExpanded(true);
    }
  }, [routerInitialIsExpanded, onToggleExpanded]);
  const {
    loading: basicDetailsLoading,
    data: basicDetails
  } = usePDPBasicDetails();
  const {
    loading: sizesLoading,
    data
  } = useProductAvailableSizes();
  const modularAvailableSizesData = useModularPDPAvailableSizes();
  const modularDetails = useModularDetails(slug);
  const pdpIsModularBYOProduct = usePdpIsModularBYOProduct();

  // modularDetails decides which list the widget renders, so treat it as
  // loading too, or the skeleton disappears and comes back while it resolves.
  const loading = basicDetailsLoading || modularDetails?.loading || !pdpIsModularBYOProduct && sizesLoading || pdpIsModularBYOProduct && modularAvailableSizesData.loading;
  const unfilteredProductsAndIds = useMemo(() => {
    if (!!modularDetails?.data?.isModularPreConfigProduct && !!modularDetails?.data?.preConfigProducts?.length && !pdpIsModularBYOProduct) {
      let modularIds = '';
      const updatedProducts = modularDetails?.data.preConfigProducts.filter(prod => !!prod && prod.id !== basicDetails?.id).filter(({
        name
      }) => {
        const lowercaseName = name?.toLowerCase() || '';
        return !!lowercaseName && !isAddOnProduct(lowercaseName);
      }).map(({
        id,
        slug: productSlug
      }) => {
        modularIds += `${id},`;
        return {
          id,
          slug: productSlug
        };
      }).filter(Boolean);
      return {
        unfilteredProducts: updatedProducts,
        productIds: modularIds
      };
    }
    if (!!data?.available_sizes && !pdpIsModularBYOProduct && !loading && !modularDetails?.loading && !modularDetails?.data?.isModularPreConfigProduct) {
      let productIdsStr = '';
      const updatedProducts_0 = data.available_sizes.filter(Boolean).filter(({
        name: name_0
      }) => {
        const lowercaseName_0 = name_0?.toLowerCase() || '';
        return !!lowercaseName_0 && !isAddOnProduct(lowercaseName_0);
      }).map(({
        id: id_0,
        slug: prodSlug
      }) => {
        if (productIdsStr.includes(id_0)) return null;
        productIdsStr += `${id_0},`;
        return {
          id: id_0,
          slug: prodSlug
        };
      }).filter(Boolean);
      return {
        unfilteredProducts: updatedProducts_0,
        productIds: productIdsStr
      };
    }
    if (!!modularAvailableSizesData?.data?.available_sizes && pdpIsModularBYOProduct && !loading) {
      let productIdsStr_0 = '';
      const updatedProducts_1 = modularAvailableSizesData.data.available_sizes.filter(Boolean).filter(({
        name: name_1
      }) => {
        const lowercaseName_1 = name_1?.toLowerCase() || '';
        return !!lowercaseName_1 && !isAddOnProduct(lowercaseName_1);
      }).map(({
        id: id_1,
        slug: prodSlug_0
      }) => {
        if (productIdsStr_0.includes(id_1)) return null;
        productIdsStr_0 += `${id_1},`;
        return {
          id: id_1,
          slug: prodSlug_0
        };
      }).filter(Boolean);
      return {
        unfilteredProducts: updatedProducts_1,
        productIds: productIdsStr_0
      };
    }
    return {};
  }, [basicDetails?.id, data?.available_sizes, pdpIsModularBYOProduct, loading, modularAvailableSizesData?.data?.available_sizes, modularDetails?.data?.isModularPreConfigProduct, modularDetails?.data?.preConfigProducts, modularDetails?.loading]);
  const {
    unfilteredProducts
  } = unfilteredProductsAndIds;

  // One request for every row's options, stock and details (CON-8446).
  const detailIds = useMemo(() => {
    const source = routerHasProductData ? routerState.productsData : unfilteredProducts;
    return (source || []).map(product => product.id).filter(Boolean).sort((a, b) => a - b);
  }, [routerHasProductData, routerState?.productsData, unfilteredProducts]);
  const {
    detailsById
  } = useAvailableSizesProductsDetails(detailIds);
  const filteredProducts = useMemo(() => {
    if (!unfilteredProducts?.length) {
      return unfilteredProducts;
    }

    // Any products that are unavailable for purchase should not be shown
    const updatedFilteredProducts = unfilteredProducts.filter(product_0 => {
      // getProductsByIDs drops ids it cannot resolve, so once the details are
      // in, a missing entry means the product is gone. Its row would sit on a
      // skeleton forever.
      if (detailsById && !detailsById[product_0.id]) {
        return false;
      }
      const availability = detailsById?.[product_0.id]?.availability;
      return !(availability?.isMarkedOOSOrDiscontinued && (availability.isProductOOS || availability.isProductDiscontinued));
    });
    if (updatedFilteredProducts.length) {
      return updatedFilteredProducts;
    }

    // Don't fall back to rows that have no details to render.
    if (detailsById) {
      return unfilteredProducts.filter(product_1 => !!detailsById[product_1.id]);
    }
    return unfilteredProducts;
  }, [unfilteredProducts, detailsById]);
  const buildYourOwnData = useMemo(() => {
    if (!!modularDetails?.data?.isModularPreConfigProduct && !!modularDetails?.data?.preConfigProducts?.length && !pdpIsModularBYOProduct) {
      let finalSlug = basicDetails?.meta.collectionSlug ? basicDetails?.meta.collectionSlug.replace('collections/', '') : '';
      finalSlug = finalSlug.length ? `${finalSlug.charAt(0).toUpperCase()}${finalSlug.slice(1)}` : '';
      return {
        showBYOSize: true,
        buildYourOwnDetails: {
          name: finalSlug,
          urlKey: `/modular/${finalSlug.toLowerCase()}-modular/`,
          dimensions: `Choose from ${filteredProducts.length} pieces to make any configuration.`
        }
      };
    }
    return {
      showBYOSize: false,
      buildYourOwnDetails: {}
    };
  }, [basicDetails?.meta?.collectionSlug, modularDetails?.data?.isModularPreConfigProduct, modularDetails?.data?.preConfigProducts?.length, pdpIsModularBYOProduct, filteredProducts]);
  const {
    showBYOSize,
    buildYourOwnDetails
  } = buildYourOwnData;

  // The router list skips the filtering above, so drop unresolvable ids here
  // too or their rows sit on a skeleton.
  const routerProducts = useMemo(() => {
    const carried = routerState?.productsData;
    if (!routerHasProductData || !detailsById) {
      return carried;
    }
    return carried.filter(product_2 => !!detailsById[product_2.id]);
  }, [routerHasProductData, routerState?.productsData, detailsById]);
  const {
    id: id_2
  } = pdpIsModularBYOProduct ? modularAvailableSizesData.data : data || {};

  // Preconfig products get their sizes from preConfigProducts, so wait for the
  // right list before deciding the widget has nothing to show. Keyed on the
  // query finishing, not on it returning rows: a product with genuinely no
  // sizes would otherwise sit on the skeleton forever (CON-8446).
  const sourceListSettled = (() => {
    if (pdpIsModularBYOProduct) {
      return !modularAvailableSizesData?.loading;
    }
    if (modularDetails?.data?.isModularPreConfigProduct) {
      return !!modularDetails?.preConfigProductsSettled;
    }
    return !sizesLoading;
  })();
  const productsToRender = routerHasProductData ? routerProducts : filteredProducts;

  // Tests what actually renders, not the source list: if every id was dropped
  // the widget has nothing to show, and the UI treats an empty list as loading.
  if (!loading && !productsToRender?.length) {
    return sourceListSettled ? <div /> : <PDPAvailableInSizesSkeleton />;
  }
  return <PDPAvailableInSizesUI buildYourOwnDetails={buildYourOwnDetails} currentProductId={id_2} isExpanded={isExpanded} loading={!routerHasProductData && loading} onToggleExpanded={onToggleExpanded} pdpIsModularBYOProduct={pdpIsModularBYOProduct} products={productsToRender} productsDetails={detailsById} showBYOSize={showBYOSize} />;
};
PDPAvailableInSizesInner.propTypes = {
  slug: PropTypes.string,
  isExpanded: PropTypes.bool,
  onToggleExpanded: PropTypes.func
};

// Without a fallback the whole widget, title included, stays blank until every
// row has loaded.
const availableInSizesFallback = <PDPAvailableInSizesSkeleton />;

// Expanded state lives outside the boundary. Anything inside it is unmounted
// while the fallback shows, so the list would close again on every suspend.
const PDPAvailableInSizes = props => {
  const $ = _c(3);
  const [isExpanded, setIsExpanded] = useState(false);
  let t0;
  if ($[0] !== isExpanded || $[1] !== props) {
    t0 = <SuspenseWithLoader fallback={availableInSizesFallback}><PDPAvailableInSizesInner {...props} isExpanded={isExpanded} onToggleExpanded={setIsExpanded} /></SuspenseWithLoader>;
    $[0] = isExpanded;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export default memo(PDPAvailableInSizes);